/**
 * Radar Address Autocomplete Service
 *
 * Provides address autocomplete functionality using the Radar.io API.
 * This service replaces Google Maps Places Autocomplete API to reduce costs
 * while maintaining similar functionality for address suggestions.
 *
 * Features:
 * - Debounced API calls to reduce request frequency
 * - Request cancellation for better performance
 * - Automatic retry logic with exponential backoff
 * - Rate limit handling
 * - Graceful error handling
 *
 * @module radar-auto-complete
 */

export type RadarAddress = {
	formattedAddress: string;
	latitude: number;
	longitude: number;
	number?: string;
	street?: string;
	city?: string;
	stateCode?: string;
	postalCode?: string;
	countryCode?: string;
	layer?: string;
	distance?: number;
};

export type RadarAutocompleteOptions = {
	countryCode?: string; // Comma-separated ISO country codes (e.g., "US,CA")
	limit?: number;
};

// Radar API config
const radarApiConfig = {
	endpoint: 'https://api.radar.io/v1/search/autocomplete',
	apiKey: 'prj_live_pk_c2565059e4940baf3843ed72acf4469dfab807d8',
	debounceTimer: 300, // Debounce delay in milliseconds
	defaultParams: {
		layers: 'address',
		limit: 5,
	},
	minCharacters: 3, // Minimum characters before API call
	maxRetries: 2,
};

/**
 * Global abort controller for canceling in-flight API requests.
 * Used to cancel previous requests when a new search is initiated.
 */
let radarAbortController: AbortController | undefined;

/**
 * Global debounce timer reference.
 * Used to clear pending debounced requests when a new search is initiated.
 */
let debounceTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Main autocomplete function with built-in debouncing and request cancellation.
 *
 * This function provides a debounced interface to the Radar API autocomplete endpoint.
 * It automatically cancels previous requests when a new search is initiated, preventing
 * race conditions and reducing unnecessary API calls.
 *
 * @param searchValue - The address search query string (minimum 3 characters)
 * @param options - Optional parameters to customize the search (country filter, result limit)
 * @returns Promise resolving to an array of Radar address suggestions
 *
 * @example
 * ```typescript
 * const addresses = await radarAddressAutocomplete('123 Main St', { countryCode: 'US' });
 * ```
 */
export async function radarAddressAutocomplete(
	searchValue: string,
	options?: RadarAutocompleteOptions,
): Promise<RadarAddress[]> {
	// Early return if search value is too short to be meaningful
	if (searchValue.length < radarApiConfig.minCharacters) {
		return [];
	}

	// Clear any existing debounce timer to reset the debounce window
	if (debounceTimer) {
		clearTimeout(debounceTimer);
	}

	// Cancel any in-flight API request to prevent race conditions
	// This ensures only the most recent search completes
	if (radarAbortController) {
		radarAbortController.abort();
	}

	// Return a promise that resolves after the debounce delay
	// This prevents excessive API calls while the user is typing
	return new Promise((resolve, reject) => {
		debounceTimer = setTimeout(async () => {
			try {
				const addresses = await performRadarSearch(searchValue, options);
				resolve(addresses);
			} catch (error: unknown) {
				reject(error);
			}
		}, radarApiConfig.debounceTimer);
	});
}

/**
 * Performs the actual Radar API search request.
 *
 * Creates a new abort controller for the request and builds the query parameters
 * according to the provided options and default configuration.
 *
 * @param searchValue - The address search query string
 * @param options - Optional parameters to customize the search
 * @returns Promise resolving to an array of Radar address suggestions
 * @throws {Error} If the API request fails after all retries
 */
async function performRadarSearch(
	searchValue: string,
	options?: RadarAutocompleteOptions,
): Promise<RadarAddress[]> {
	// Create a new abort controller for this specific request
	// This allows us to cancel the request if a new search is initiated
	// eslint-disable-next-line compat/compat
	radarAbortController = new AbortController();

	// Build query parameters for the Radar API request
	// Uses provided options or falls back to default configuration
	const parameters = new URLSearchParams({
		query: searchValue, // Radar API requires 'query' parameter, not 'searchValue'
		layers: radarApiConfig.defaultParams.layers,
		limit: String(options?.limit ?? radarApiConfig.defaultParams.limit),
	});

	// Only add countryCode if explicitly provided in options
	if (options?.countryCode) {
		parameters.append('countryCode', options.countryCode);
	}

	// Delegate to retry-enabled search function
	return performRadarSearchWithRetry(parameters, 0);
}

/**
 * Performs Radar API search with automatic retry logic.
 *
 * Handles network errors, rate limiting, and other transient failures by
 * automatically retrying with exponential backoff. Abort errors are handled
 * silently as they indicate intentional request cancellation.
 *
 * @param parameters - URL search parameters for the API request
 * @param retryCount - Current retry attempt number (starts at 0)
 * @returns Promise resolving to an array of Radar address suggestions
 * @throws {Error} If the API request fails after all retries are exhausted
 *
 * @remarks
 * - Rate limit errors (429) are retried with a 1-second delay
 * - Network errors use exponential backoff: 500ms, 1000ms, 1500ms, etc.
 * - Abort errors (cancelled requests) return empty array silently
 * - Maximum retries are controlled by radarApiConfig.maxRetries
 */
async function performRadarSearchWithRetry(
	parameters: URLSearchParams,
	retryCount: number,
): Promise<RadarAddress[]> {
	try {
		// Make the API request with authentication headers
		const response = await fetch(
			`${radarApiConfig.endpoint}?${parameters.toString()}`,
			{
				method: 'GET',
				headers: {
					// Radar API uses the publishable key in the Authorization header
					authorization: radarApiConfig.apiKey,
					'content-type': 'application/json',
				},
				// Attach abort signal to allow request cancellation
				signal: radarAbortController?.signal,
			},
		);

		// Handle non-OK responses
		if (!response.ok) {
			// Special handling for rate limit errors (429 Too Many Requests)
			if (response.status === 429) {
				console.warn('Radar API rate limit reached');
				// Retry after a delay if we haven't exceeded max retries
				if (retryCount < radarApiConfig.maxRetries) {
					// Wait 1 second before retrying rate-limited requests
					await new Promise<void>(resolve => {
						setTimeout(() => {
							resolve();
						}, 1000);
					});
					return await performRadarSearchWithRetry(parameters, retryCount + 1);
				}
			}

			// For other HTTP errors, throw immediately (no retry)
			throw new Error(`Radar API error: ${response.status} ${response.statusText}`);
		}

		// Parse response and extract addresses array
		// Radar API returns { addresses: RadarAddress[] } structure
		const data = await response.json() as {addresses?: RadarAddress[]};
		return data.addresses ?? [];
	} catch (error: unknown) {
		// Handle abort errors silently - these are intentional cancellations
		// and should not be treated as failures
		if (error instanceof Error && error.name === 'AbortError') {
			return [];
		}

		// Retry logic for network errors and other transient failures
		const errorMessage = error instanceof Error ? error.message : String(error);
		// Only retry if we haven't exceeded max retries and it's not a permanent API error
		if (retryCount < radarApiConfig.maxRetries && !errorMessage.includes('Radar API error')) {
			// Exponential backoff: delay increases with each retry attempt
			// First retry: 500ms, second retry: 1000ms, etc.
			const delay = 500 * (retryCount + 1);
			await new Promise<void>(resolve => {
				setTimeout(() => {
					resolve();
				}, delay);
			});
			return performRadarSearchWithRetry(parameters, retryCount + 1);
		}

		// Log error for debugging and re-throw if all retries exhausted
		console.error('Radar address autocomplete error:', error);
		throw error instanceof Error ? error : new Error(String(error));
	}
}
